import React from 'react';
import { Stack, Pagination, Typography, MenuItem, Select, FormControl } from '@mui/material';

/**
 * Reusable pagination bar.
 *
 * Props:
 *   page       – current page (1-based)
 *   pages      – total pages
 *   total      – total record count
 *   limit      – current page size
 *   onPage     – (newPage) => void
 *   onLimit    – (newLimit) => void   (optional — hides selector if omitted)
 *   limitOptions – array of numbers  (default [20, 50, 100])
 */
export default function PaginationBar({
  page, pages, total, limit,
  onPage, onLimit,
  limitOptions = [20, 50, 100],
}) {
  if (!pages || pages <= 1) return null;

  const from = (page - 1) * limit + 1;
  const to   = Math.min(page * limit, total);

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems="center"
      spacing={1.5}
      sx={{ mt: 2.5, px: 0.5 }}
    >
      {/* Record range */}
      <Typography variant="body2" color="text.secondary">
        Showing {from}–{to} of {total}
      </Typography>

      {/* Page selector */}
      <Pagination
        count={pages}
        page={page}
        onChange={(_, v) => onPage(v)}
        color="primary"
        size="small"
        siblingCount={1}
        boundaryCount={1}
        showFirstButton
        showLastButton
      />

      {/* Per-page selector */}
      {onLimit && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" color="text.secondary">Per page:</Typography>
          <FormControl size="small">
            <Select
              value={limit}
              onChange={e => { onLimit(Number(e.target.value)); onPage(1); }}
              sx={{ fontSize: '0.8125rem', minWidth: 70 }}
            >
              {limitOptions.map(n => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      )}
    </Stack>
  );
}
